import React, { useState } from 'react';
import api from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Lock, RefreshCw, Minus, Plus, AlertTriangle } from 'lucide-react';
import { PilotFeedbackWidget } from '@/components/pilot/PilotFeedbackWidget';

/**
 * SleepoverAdjustmentModal
 *
 * Shown when the API returns 409 SHIFT_BILLING_LOCKED on an active-hours
 * add/delete. Lets the user choose one of the three Phase 2C adjustment
 * paths (CORRECTION, CREDIT_ONLY, ADD_AFTER_BILLING) and routes to the
 * correct endpoint. Always includes an audit-trail reason.
 *
 * Props:
 *   open, onClose, onSuccess  — dialog controls
 *   shiftId                   — required
 *   mode                      — 'correct' | 'credit' | 'add' (initial mode)
 *   interval                  — required for correct/credit: {id, start_time, end_time, reason}
 *   lockReason                — object returned by the 409 detail.details (optional)
 */
export function SleepoverAdjustmentModal({
  open, onClose, onSuccess, shiftId, mode, interval, lockReason,
}) {
  const [activeMode, setActiveMode] = useState(mode || 'correct');
  const [form, setForm] = useState({
    start_time: interval?.start_time || '23:00',
    end_time: interval?.end_time || '23:30',
    reason: interval?.reason || '',
    adjustment_reason: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submittedAdj, setSubmittedAdj] = useState(null);  // Feedback state after save

  const reset = () => {
    setForm({
      start_time: interval?.start_time || '23:00',
      end_time: interval?.end_time || '23:30',
      reason: interval?.reason || '',
      adjustment_reason: '',
    });
  };

  React.useEffect(() => {
    if (open) {
      setActiveMode(mode || 'correct');
      setSubmittedAdj(null);
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, interval?.id]);

  const validate = () => {
    if (!form.adjustment_reason || form.adjustment_reason.trim().length < 3) {
      toast.error('Audit reason required (min 3 characters)');
      return false;
    }
    if (activeMode !== 'credit') {
      if (!form.start_time || !form.end_time) {
        toast.error('Provide start and end time');
        return false;
      }
      if (!form.reason || form.reason.trim().length < 3) {
        toast.error('Interval reason required (min 3 characters)');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      let res;
      if (activeMode === 'correct') {
        res = await api.post(
          `/roster/shifts/${shiftId}/active-hours/${interval.id}/correct`,
          form,
        );
      } else if (activeMode === 'credit') {
        res = await api.post(
          `/roster/shifts/${shiftId}/active-hours/${interval.id}/credit`,
          { adjustment_reason: form.adjustment_reason },
        );
      } else {
        res = await api.post(
          `/roster/shifts/${shiftId}/active-hours/add-adjustment`,
          form,
        );
      }
      const net = res.data?.data?.net_amount;
      toast.success(
        `Adjustment saved${net !== undefined ? ` · net $${net.toFixed(2)}` : ''}`,
      );
      // Retain state so the feedback widget can render; call onSuccess but defer close.
      setSubmittedAdj(res.data?.data || {});
      onSuccess?.(res.data?.data);
    } catch (e) {
      const data = e.response?.data || {};
      toast.error(data.error?.message || data.detail?.message || data.detail || 'Failed to save adjustment');
    } finally {
      setSubmitting(false);
    }
  };

  const kinds = [
    { key: 'correct', label: 'Correction', icon: RefreshCw,
      desc: 'Replace start/end of this interval. Creates credit + debit pair.',
      disabled: !interval },
    { key: 'credit', label: 'Credit-only (remove)', icon: Minus,
      desc: 'Remove the interval after billing. Creates credit line only.',
      disabled: !interval },
    { key: 'add', label: 'Add after billing', icon: Plus,
      desc: 'Add a NEW interval after the shift was billed. Creates debit line only.',
      disabled: false },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="sleepover-adjustment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-600" />
            Shift is billing-locked — record an adjustment
          </DialogTitle>
          <DialogDescription>
            This sleep-over shift has already been invoiced or claimed.
            Direct edits are disabled for NDIS audit compliance.
            Use one of the paths below to create a credit-note adjustment.
          </DialogDescription>
        </DialogHeader>

        {lockReason && (
          <Alert className="bg-amber-50 border-amber-300">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription className="text-xs">
              <span className="font-medium">Lock source:</span> {lockReason.source || 'unknown'}
              {lockReason.status && <> · <Badge variant="outline" className="text-[10px]">{lockReason.status}</Badge></>}
              {lockReason.claim_no && <> · claim {lockReason.claim_no}</>}
              {lockReason.invoice_id && <> · invoice {String(lockReason.invoice_id).slice(0, 8)}…</>}
            </AlertDescription>
          </Alert>
        )}

        {!submittedAdj && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="adjustment-kind-picker">
          {kinds.map(({ key, label, icon: Icon, desc, disabled }) => (
            <button
              type="button"
              key={key}
              disabled={disabled}
              onClick={() => setActiveMode(key)}
              className={`text-left p-3 rounded-md border transition ${
                activeMode === key
                  ? 'border-primary bg-primary/5'
                  : 'border-slate-200 hover:border-slate-300'
              } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
              data-testid={`adjustment-kind-${key}`}
            >
              <div className="flex items-center gap-1.5 font-medium text-sm">
                <Icon className="w-3.5 h-3.5" /> {label}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">{desc}</p>
            </button>
          ))}
        </div>
        )}

        {!submittedAdj && (
        <div className="space-y-3">
          {activeMode !== 'credit' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="adj-start" className="text-xs">Start</Label>
                  <Input
                    id="adj-start"
                    type="time"
                    value={form.start_time}
                    onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                    data-testid="adjustment-start"
                  />
                </div>
                <div>
                  <Label htmlFor="adj-end" className="text-xs">End</Label>
                  <Input
                    id="adj-end"
                    type="time"
                    value={form.end_time}
                    onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                    data-testid="adjustment-end"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="adj-reason" className="text-xs">Interval reason</Label>
                <Input
                  id="adj-reason"
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  placeholder="e.g. medication wake-up"
                  data-testid="adjustment-interval-reason"
                />
              </div>
            </>
          )}
          <div>
            <Label htmlFor="adj-audit" className="text-xs flex items-center gap-1">
              Audit reason (required) <Badge variant="outline" className="text-[9px]">audit trail</Badge>
            </Label>
            <Textarea
              id="adj-audit"
              value={form.adjustment_reason}
              onChange={(e) => setForm({ ...form, adjustment_reason: e.target.value })}
              placeholder="Why is this adjustment being made?"
              rows={2}
              data-testid="adjustment-audit-reason"
            />
          </div>
        </div>
        )}

        {submittedAdj && (
          <div className="rounded-md border border-green-200 bg-green-50 p-3 space-y-2" data-testid="adjustment-success">
            <div className="flex items-start gap-2">
              <div className="flex-1 text-xs">
                <p className="font-medium text-green-800">
                  Adjustment recorded · net ${Number(submittedAdj.net_amount || 0).toFixed(2)}
                </p>
                <p className="text-[11px] text-green-700">
                  The shift history now shows this correction. Close when you're ready.
                </p>
              </div>
            </div>
            <PilotFeedbackWidget
              feature="sleepover-adjustment-modal"
              context={{ shift_id: shiftId, adjustment_kind: submittedAdj.kind }}
              onSubmitted={() => { /* no-op */ }}
            />
          </div>
        )}

        <DialogFooter>
          {submittedAdj ? (
            <Button onClick={onClose} data-testid="adjustment-close-after-save">Close</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} data-testid="adjustment-cancel">Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="adjustment-submit">
                {submitting ? 'Saving…' : 'Record adjustment'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
